import { useEffect, useImperativeHandle, useState, forwardRef } from 'react';
import { Form, Collapse, Typography, Divider, Button, Tooltip } from 'antd';
import { CloseCircleOutlined, GiftOutlined } from '@ant-design/icons';
import { LoyaltyBadge, type LoyaltyRedeemResult } from './LoyaltyBadge';
import type { FormInstance } from 'antd';
import { OrderTypeToggle } from '../OrderTypeToggle';
import { LiveSessionPicker } from '../LiveSessionPicker';
import { CustomerSearchField } from './CustomerSearchField';
import { UserTypeToggle } from './UserTypeToggle';
import { DeliverySection } from './DeliverySection';
import { InvoiceSection } from './InvoiceSection';
import { VoucherModal, type VoucherItem } from './VoucherModal';
import { FIELDS_RESET_ON_USER_TYPE_CHANGE } from './customerFormSchema';
import type {
  OrderType,
  CommissionStaff,
  FulfillmentMode,
  UserType,
  PosCustomerSearchItem,
} from '../../types';
import './CustomerSidebar.css';

const { Text } = Typography;

export interface CustomerSidebarHandle {
  expandAll(): void;
}

interface Props {
  form: FormInstance;
  orderType: OrderType;
  onOrderTypeChange: (v: OrderType) => void;
  selectedStoreId?: string;
  selectedSessionId?: string;
  onSessionChange: (sessionId: string, commissionStaff: CommissionStaff[]) => void;
  hasItems: boolean;
  onClearItems: () => void;
  fulfillmentMode?: FulfillmentMode;
  orderTotal?: number;
}

const INLINE_LABEL_STYLE: React.CSSProperties = {
  fontSize: 12,
  color: '#555',
  fontWeight: 600,
  flexShrink: 0,
  width: 110,
};

export const CustomerSidebar = forwardRef<CustomerSidebarHandle, Props>(function CustomerSidebar(
  {
    form,
    orderType,
    onOrderTypeChange,
    selectedStoreId,
    selectedSessionId,
    onSessionChange,
    orderTotal = 0,
    fulfillmentMode = 'INSTANT',
  },
  ref,
) {
  const userType = (Form.useWatch('user_type', form) as UserType) ?? 'personal';
  const cityId = Form.useWatch('customer_city_id', form) as string | undefined;
  const vouchers = (Form.useWatch('vouchers', form) as VoucherItem[] | undefined) ?? [];
  const loyaltyRedeemPoints = Form.useWatch('loyalty_redeem_points', form) as number | undefined;
  const loyaltyRedeemValue = Form.useWatch('loyalty_redeem_value', form) as number | undefined;

  const [activeKeys, setActiveKeys] = useState<string[]>(['delivery']);
  const [voucherModalOpen, setVoucherModalOpen] = useState(false);

  // Default values once (mount only).
  useEffect(() => {
    form.setFieldsValue({
      user_type: form.getFieldValue('user_type') ?? 'personal',
      invoice_same_as_delivery: form.getFieldValue('invoice_same_as_delivery') ?? false,
    });
  }, []);

  useImperativeHandle(ref, () => ({
    expandAll: () => setActiveKeys(['delivery', 'invoice']),
  }));

  function handleUserTypeChange(next: UserType) {
    const reset: Record<string, unknown> = {};
    for (const k of FIELDS_RESET_ON_USER_TYPE_CHANGE) {
      reset[k] = k === 'invoice_same_as_delivery' ? false : undefined;
    }
    reset['_cityName'] = undefined;
    reset['_wardName'] = undefined;
    reset['user_type'] = next;
    form.setFieldsValue(reset);
  }

  const customerUserId = Form.useWatch('customer_user_id', form) as string | undefined;

  function handleCustomerSelect(item: PosCustomerSearchItem) {
    const u = item.user;
    const a = item.address;
    form.setFieldsValue({
      customer_user_id: u._id || undefined,
      invoice_same_as_delivery: false,
      user_type: u.user_type ?? 'personal',
      customer_phone: a.phone || u.phone || '',
      customer_name: a.name || u.name || '',
      customer_email: a.email || u.email || '',
      citizen_id: a.citizen_id || '',
      customer_address: a.address || u.address || '',
      customer_city_id: a.city_id || u.city_id || undefined,
      _cityName: a.city?.name ?? '',
      customer_ward_id: a.ward_id || u.ward_id || undefined,
      _wardName: a.ward?.name ?? '',
      customer_birthday: u.birthday ? u.birthday.substring(0, 10) : undefined,
      invoice_name: u.invoice_organization_name || '',
      invoice_tax_id: u.tax_code || '',
      invoice_address: u.address || '',
      invoice_email: u.tax_email || '',
      buyer_name: u.invoice_buyer_name || '',
      buyer_cccd: u.invoice_buyer_cccd || '',
      misa_account_object_id: u.misa_account_object_id || undefined,
      ...(item.account_manager?.staff_id
        ? { care_staff_id: item.account_manager.staff_id }
        : {}),
    });
  }

  return (
    <div
      style={{
        width: '100%',
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        background: '#fafafa',
      }}
    >
      <div
        style={{
          padding: '8px 12px',
          background: '#f0f0f5',
          borderBottom: '1px solid #e0e0e0',
          fontWeight: 700,
          fontSize: 11,
          color: '#555',
          textTransform: 'uppercase',
          letterSpacing: '0.5px',
        }}
      >
        Thông tin đơn hàng
      </div>

      <div
        className="customer-sidebar-scroll"
        style={{
          flex: 1,
          minHeight: 0,
          overflowY: 'scroll',
          padding: '8px 12px',
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
        }}
      >
        {/* Loại đơn hàng — inline label + toggle */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Text style={INLINE_LABEL_STYLE}>Loại đơn hàng</Text>
          <div style={{ flex: 1, minWidth: 0 }}>
            <OrderTypeToggle value={orderType} onChange={onOrderTypeChange} />
          </div>
        </div>

        {/* Phiên Livestream — chỉ hiện khi orderType=LIVESTREAM */}
        {orderType === 'LIVESTREAM' && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Text style={INLINE_LABEL_STYLE}>Phiên Live</Text>
            <div style={{ flex: 1, minWidth: 0 }}>
              <LiveSessionPicker
                storeId={selectedStoreId}
                value={selectedSessionId}
                onChange={onSessionChange}
              />
            </div>
          </div>
        )}

        {/* Loại khách hàng — inline label + toggle */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Text style={INLINE_LABEL_STYLE}>Loại khách hàng</Text>
          <div style={{ flex: 1, minWidth: 0 }}>
            <UserTypeToggle value={userType} onChange={handleUserTypeChange} />
          </div>
        </div>

        {/* Tìm khách hàng (floating label) + nút Gift bên cạnh */}
        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <CustomerSearchField label="Tìm khách hàng" onSelect={handleCustomerSelect} />
          </div>
          <Tooltip title="Chọn khuyến mãi / Voucher">
            <Button
              icon={<GiftOutlined style={{ fontSize: 18 }} />}
              onClick={() => setVoucherModalOpen(true)}
              style={{ height: 36, width: 40, flexShrink: 0 }}
            />
          </Tooltip>
        </div>

        {/* Chip các voucher đang chọn */}
        {vouchers.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {vouchers.map((v) => (
              <div
                key={v.code}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  padding: '6px 10px',
                  background: '#fff8f9',
                  border: '1px solid #a4193d',
                  borderRadius: 6,
                  fontSize: 12,
                }}
              >
                <GiftOutlined style={{ color: '#a4193d' }} />
                <span style={{ flex: 1, minWidth: 0, fontWeight: 600, color: '#a4193d' }}>
                  {v.code} - {v.label}
                </span>
                <CloseCircleOutlined
                  onClick={() =>
                    form.setFieldValue(
                      'vouchers',
                      vouchers.filter((x) => x.code !== v.code),
                    )
                  }
                  style={{ color: '#a4193d', cursor: 'pointer', fontSize: 16 }}
                  role="button"
                  aria-label={`Bỏ voucher ${v.code}`}
                />
              </div>
            ))}
          </div>
        )}

        {/* Loyalty points badge — visible when a customer is selected */}
        {customerUserId && (
          <LoyaltyBadge
            customerId={customerUserId}
            onRedeem={(result: LoyaltyRedeemResult) => {
              form.setFieldsValue({
                loyalty_redeem_points: result.points,
                loyalty_redeem_value: result.value,
              });
            }}
            redeemedPoints={loyaltyRedeemPoints}
            redeemedValue={loyaltyRedeemValue}
          />
        )}

        {/* Hidden Form.Items so Form has the keys */}
        <Form.Item name="customer_user_id" hidden>
          <input />
        </Form.Item>
        <Form.Item name="user_type" hidden>
          <input />
        </Form.Item>
        <Form.Item name="_cityName" hidden>
          <input />
        </Form.Item>
        <Form.Item name="_wardName" hidden>
          <input />
        </Form.Item>
        <Form.Item name="vouchers" hidden>
          <input />
        </Form.Item>
        <Form.Item name="loyalty_redeem_points" hidden>
          <input />
        </Form.Item>
        <Form.Item name="loyalty_redeem_value" hidden>
          <input />
        </Form.Item>

        <Divider style={{ margin: '4px 0' }} />

        <Collapse
          activeKey={activeKeys}
          onChange={(keys) => setActiveKeys(keys as string[])}
          ghost
          size="small"
          items={[
            {
              key: 'delivery',
              label: 'Thông tin giao nhận',
              // EG-1754: forceRender để Form.Item luôn mount kể cả khi panel
              // đóng — nếu lazy-mount thì validateFields() lúc checkout bỏ qua
              // các field chưa mount (CCCD/MST… xuất hoá đơn), cho đơn lọt qua
              // dù dữ liệu sai/thiếu.
              forceRender: true,
              children: (
                <DeliverySection
                  form={form}
                  fulfillmentMode={fulfillmentMode}
                  storeId={selectedStoreId}
                  cityId={cityId}
                />
              ),
            },
            {
              key: 'invoice',
              label: 'Thông tin xuất hoá đơn',
              forceRender: true,
              children: <InvoiceSection form={form} userType={userType} />,
            },
          ]}
        />
      </div>

      <VoucherModal
        open={voucherModalOpen}
        onClose={() => setVoucherModalOpen(false)}
        orderTotal={orderTotal}
        onConfirm={(vs) => {
          form.setFieldValue('vouchers', vs);
          setVoucherModalOpen(false);
        }}
        selectedCodes={vouchers.map((v) => v.code)}
      />
    </div>
  );
});
